import { FC } from 'react';
import { ReactSortable } from 'react-sortablejs';

import { MediaListItem } from 'application/components/MediaList/MediaListItem';
import { MediaListItemIconsUploader } from 'application/components/MediaList/MediaListItemIcons';
import FileUtils from 'application/utils/FileUtils';

import { PromoUploadStatusType } from '@entities/adCampaign';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import IconGripVertical from '@zvuk-b2b/react-uikit/ui/icons/GripVertical';
import TimeInput, { defaultTimeInputValidator, TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';

import { ELEMENT_IDS, MEDIA_FILE_TYPE } from './consts';
import styles from './index.module.scss';
import { TEXTS } from '../texts';
import { ExactTimeMediaUploaderProps } from './types';

const ExactTimeMediaUploader: FC<ExactTimeMediaUploaderProps> = ({
  acceptValue,
  uploadButtonText,
  uploadDescription,
  inputRef,
  items,
  timeById,
  player,
  onUploadClick,
  onFilesChange,
  onTimeChange,
  onTimeBlur,
  onPlayClick,
  onRemoveClick,
  onRetryClick,
  onReorder,
}) => (
  <div className={styles.root}>
    <Button
      elementId={ELEMENT_IDS.uploadButton}
      type={ButtonType.SECONDARY}
      onClick={onUploadClick}>
      {uploadButtonText}
    </Button>

    <input
      ref={inputRef}
      data-testid={ELEMENT_IDS.uploadInputTestId}
      type="file"
      multiple
      accept={acceptValue}
      className={styles.fileInputNative}
      onChange={onFilesChange}
    />

    <p className={styles.description}>{uploadDescription}</p>
    {items.length > 0 && <p className={styles.exactTimeHint}>{TEXTS.EXACT_TIME_HINT}</p>}

    <ReactSortable<PromoUploadStatusType & { id: string }>
      tag="ul"
      list={items as (PromoUploadStatusType & { id: string })[]}
      handle={`.${styles.dragHandle}`}
      setList={onReorder}
      className={styles.list}>
      {items.map((item) => {
        const itemId = item.id || '';
        const filename = item.file?.name || item.title || item.filename || '';
        const fileUrl = FileUtils.getMediafileUrl(item.sourcefiles) || '';
        const isMediafilePlaying = player.fileName === filename && player.fileUuid === itemId;
        const mediaFileProgress = isMediafilePlaying ? player.progress : undefined;
        const isRemoveVisible = !item.error && !item.isLoading && itemId;

        return (
          <li
            key={itemId || filename}
            className={styles.itemExact}>
            <span
              className={styles.dragHandle}
              data-testid={`${ELEMENT_IDS.dragHandlePrefix}_${itemId || filename}`}>
              <IconGripVertical />
            </span>
            {itemId && (
              <TimeInput
                elementId={`${ELEMENT_IDS.timeInputPrefix}_${itemId}`}
                containerClassName={styles.timeInput}
                maxLength={5}
                mask="00:00"
                pattern={TIME_PATTERN}
                placeholder={TEXTS.FILE_TIME_DEFAULT_VALUE}
                value={timeById[itemId] ?? item.time ?? ''}
                inputValidator={defaultTimeInputValidator}
                onBlur={() => onTimeBlur(itemId)}
                onChange={(value) => onTimeChange(itemId, value)}
              />
            )}

            <MediaListItem
              title={filename}
              isLoading={item.isLoading}
              isPaused={player.isPaused}
              isMediafilePlaying={isMediafilePlaying}
              progress={mediaFileProgress}
              type={MEDIA_FILE_TYPE}
              className={styles.mediaItem}
              titleClassName={styles.mediaItemTitle}
              progressSliderClassName={styles.progressSlider}
              onClickPlay={() => onPlayClick(itemId, filename, fileUrl)}
              onClickPause={player.pause}
              onClickResume={player.resume}
              onChangeProgress={player.changeProgress}>
              <MediaListItemIconsUploader
                id={`${ELEMENT_IDS.mediaIconsPrefix}_${itemId}`}
                isRemove={Boolean(isRemoveVisible)}
                isError={Boolean(item.error)}
                isMultiple
                onRemove={() => onRemoveClick(itemId)}
                onRetry={() => onRetryClick(itemId)}
              />
            </MediaListItem>
          </li>
        );
      })}
    </ReactSortable>
  </div>
);

export default ExactTimeMediaUploader;
